import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Vacation } from "domain/entities/vacations";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { differenceInCalendarDays } from "date-fns";

/**
 * Propriedades para o componente VacationList.
 */
interface VacationListProps {
  /** Uma lista de objetos de férias a serem exibidos na tabela. */
  vacations: Vacation[];
  /** Um booleano que indica se a tabela está em estado de carregamento. */
  isLoading: boolean;
  /**
   * Função de callback que é chamada quando o utilizador clica no ícone para cancelar umas férias.
   * @param vacationPlan - As férias a serem canceladas.
   */
  onCancel: (vacationPlan: Vacation) => void;
}

/**
 * Verifica se um agendamento de férias pode ser cancelado com base na sua data de início.
 * A regra de negócio define que umas férias só podem ser canceladas se o seu mês de início
 * for posterior ao mês atual, no mesmo ano, ou se for num ano futuro.
 * @param vacation - O objeto de férias a ser verificado.
 * @returns {boolean} - Retorna `true` se as férias puderem ser canceladas, caso contrário `false`.
 */
// const canCancelVacation = (vacation: Vacation): boolean => {
//   // Garante que o objeto vacation e a data de início existem para evitar erros.
//   if (!vacation || !vacation.startAt) {
//     return false;
//   }

//   const startDate = new Date(vacation.startAt);

//   // Se a data for inválida (ex: string em formato incorreto), não pode ser cancelada.
//   if (isNaN(startDate.getTime())) {
//     return false;
//   }

//   const now = new Date();
//   return (
//     now.getFullYear() < startDate.getFullYear() ||
//     (startDate.getFullYear() === now.getFullYear() &&
//       now.getMonth() < startDate.getMonth())
//   );
// };

/**
 * O componente `VacationList` é responsável por renderizar uma lista de agendamentos de férias
 * utilizando o componente `DataTable` da biblioteca PrimeReact.
 *
 * Funcionalidades:
 * - Exibe os detalhes de cada agendamento, como datas, duração e status.
 * - Mostra um indicador de carregamento enquanto os dados estão a ser obtidos.
 * - Apresenta opções condicionais para cada agendamento, como cancelar ou ver o motivo da rejeição.
 * - Utiliza a função `canCancelVacation` para determinar se um agendamento pode ser cancelado.
 *
 * @param {VacationListProps} props - As propriedades do componente.
 * @returns {React.FC} Um componente funcional React que renderiza a tabela de férias.
 */
const VacationList: React.FC<VacationListProps> = ({
  vacations,
  isLoading,
  onCancel,
}) => {
  return (
    <div className="timesheet-4-cols">
      <DataTable
        value={vacations}
        loading={isLoading}
        className="p-datatable-sm"
        paginator
        paginatorLeft
        rows={10}
        sortField={"startAt"}
        sortOrder={-1}
        responsiveLayout="scroll"
      >
        <Column
          field="createdAt"
          header="Data da Solicitação"
          body={(rowData) => formatDateToPT(rowData.createdAt)}
        />
        <Column
          field="startAt"
          header="Início"
          body={(rowData) => formatDateToPT(rowData.startAt)}
        />
        <Column
          field="endAt"
          header="Fim"
          body={(rowData) => formatDateToPT(rowData.endAt)}
        />
        <Column
          header="Dias Usados"
          headerStyle={{ textAlign: "center" }}
          body={(rowData) => {
            return `${rowData.days} dias`;
          }}
        />
        <Column
          field="status"
          header="Status"
          body={(rowData) => {
            if (rowData.status == "Aprovado" && rowData.rejectionReason)
              return `${rowData.status} *`
            else
              return rowData.status
          }}
        />

        <Column
          field="rejectionReason"
          header="Opções"
          style={{ textAlign: "center" }}
          headerStyle={{ display: "flex", justifyContent: "center" }}
          body={(rowData) => {
            // const isCancellable = canCancelVacation(rowData);

            // if (rowData.status === "Não Aprovado") {
            //   let infoMessage = rowData.rejectionReason
            //     ? "Este agendamento deve ser removido para uma nova marcação de férias, em dias coincidentes. Motivo: " +
            //       rowData.rejectionReason
            //     : "Este agendamento deve ser removido para uma nova marcação de férias, em dias coincidentes. ";

            //   // if (!isCancellable) {
            //   //   infoMessage += "\nNo entanto, só se podem cancelar agendamentos até ao mês anterior da ocorrência. Para mais informações, entrar em contacto com os RH.";
            //   // }

            //   // return (
            //   //   <div className="spacing-icons">
            //   //     {rowData.rejectionReason && (
            //   //       <i
            //   //         className="pi pi-info-circle cursor-pointer"
            //   //         // title={infoMessage}
            //   //         data-pr-tooltip={rowData.rejectionReason}
            //   //         data-pr-position="top"
            //   //       />
            //   //     )}

            //   //     {/* {isCancellable && ( */}
            //   //       {/* <i
            //   //         title="Cancelar férias"
            //   //         className="pi pi-times cursor-pointer"
            //   //         onClick={() => onCancel(rowData.id)}
            //   //       /> */}
            //   //     {/* )} */}
            //   //   </div>
            //   // );
            // }

            // const infoMessage = isCancellable
            //   ? "Pode cancelar este agendamento de férias."
            //   : "Só se podem cancelar agendamentos até ao mês anterior da ocorrência. Para mais informações, entrar em contacto com os RH.";
            // return isCancellable ? (

            let messageToShow = undefined;
            if (rowData.status == "Aprovado" && rowData.rejectionReason)
              messageToShow = `O colaborador pediu para cancelar este período de férias mas o seu manager não aprovou esse cancelamento.\n\nMotivo: ${rowData.rejectionReason}`
            else if (rowData.status == "Não Aprovado")
              messageToShow = `O seu manager não aprovou o pedido de férias neste período.\n\nMotivo: ${rowData.rejectionReason}`
            else if (rowData.status == "Cancelado")
              messageToShow = `O colaborador pediu para cancelar este período de férias e o seu manager aceitou o cancelamento.`
            else if (rowData.status == "Cancelamento Pendente")
              messageToShow = `Pedido efetuado pelo colaborador para cancelar este período de férias que aguarda aprovação do seu manager.`

            let alertToShow = undefined;
            if (rowData.daysNextYear > 0) {
              alertToShow = `Este período de férias está a utilizar ${rowData.daysNextYear} dia${rowData.daysNextYear > 1 ? 's' : ''} de férias do ano civil seguinte.`
            }

            if (messageToShow !== undefined && alertToShow !== undefined) {
              return (
                <>
                  <i
                    className="pi pi-info-circle cursor-pointer"
                    title={messageToShow}
                  />
                  {" "}
                  <i
                    className="pi pi-exclamation-triangle cursor-pointer"
                    title={alertToShow}
                  />
                </>

              )
            }

            if (messageToShow !== undefined) {
              return (
                <i
                  className="pi pi-info-circle cursor-pointer"
                  title={messageToShow}
                />
              )
            }

            if (alertToShow !== undefined) {
              return (
                <>
                  <i
                    className="pi pi-exclamation-triangle cursor-pointer"
                    title={alertToShow}
                  />

                  {(
                    (rowData.status === "Aprovado" ||
                      rowData.status === "A Aprovar") &&
                    rowData.startAt &&
                    new Date(rowData.startAt) >=
                    new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
                  )
                    && (
                      <i
                        title="Cancelar férias"
                        className="pi pi-times cursor-pointer ml-2"
                        onClick={() => onCancel(rowData)}
                      />
                    )}
                </>
              )
            }

            if ((rowData.status === "Aprovado" || rowData.status === "A Aprovar") &&
              rowData.startAt &&
              new Date(rowData.startAt) >=
              new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)) {
              return (
                <i
                  title="Cancelar férias"
                  className="pi pi-times cursor-pointer"
                  onClick={() => onCancel(rowData)}
                />
              )
            }



            // ) : (
            //   <i
            //     className="pi pi-info-circle cursor-pointer"
            //     // title={infoMessage}
            //     data-pr-tooltip={infoMessage}
            //     data-pr-position="top"
            //   />
            // );
          }}
        />
      </DataTable>
    </div>
  );
};

export default VacationList;
